import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import {
  CalendarPlus, Plus, FileX2, FileText, Download,
  ArrowRight, Clock, RefreshCw, PencilLine,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DatePicker } from '../ui/DatePicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

import { getDateFnsLocale } from '../../utils/attendanceUtils';

// ─── Assign Planning Dialog ───────────────────────────────────────────────────

export const AssignPlanningDialog = ({
  open, onClose,
  employee,
  schedules,
  assignScheduleId, setAssignScheduleId,
  assignStartDate, setAssignStartDate,
  assignEndDate, setAssignEndDate,
  assigning, onAssign,
}) => {
  const { t } = useTranslation();
  return (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CalendarPlus className="h-5 w-5 text-muted-foreground" />
          {t('attComp.dialogs.assignTitle')}
        </DialogTitle>
      </DialogHeader>
      {employee && (
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            {t('attComp.dialogs.employeePrefix')} <span className="font-semibold text-foreground">{employee.first_name} {employee.last_name}</span>
          </p>

          <div className="space-y-2">
            <Label>{t('attComp.dialogs.planning')}</Label>
            <Select value={assignScheduleId} onValueChange={setAssignScheduleId}>
              <SelectTrigger><SelectValue placeholder={t('attComp.dialogs.choosePlanning')} /></SelectTrigger>
              <SelectContent>
                {schedules.map(s => (
                  <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {assignScheduleId && (() => {
              const sel = schedules.find(s => String(s.id) === assignScheduleId);
              const slot = sel?.time_slots?.[0];
              if (!slot) return null;
              const fmt = (v) => v ? v.substring(0, 5) : null;
              const start = fmt(slot.start_time);
              const end = fmt(slot.end_time);
              if (!start) return null;
              return (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <span className="font-mono">{start}</span>
                  <ArrowRight className="h-3 w-3" />
                  <span className="font-mono">{end || '?'}</span>
                </p>
              );
            })()}
          </div>

          <div className="flex items-center gap-3">
            <div className="space-y-2 flex-1">
              <Label>{t('attComp.dialogs.fromLabel')}</Label>
              <DatePicker value={assignStartDate} onChange={setAssignStartDate} />
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground mt-6" />
            <div className="space-y-2 flex-1">
              <Label>{t('attComp.dialogs.toLabel')}</Label>
              <DatePicker value={assignEndDate} minDate={assignStartDate} onChange={setAssignEndDate} />
            </div>
          </div>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={onAssign} disabled={!assignScheduleId || assigning}
          className="gap-2">
          {assigning ? <><RefreshCw className="h-4 w-4 animate-spin" /> {t('attComp.dialogs.assigning')}</> : t('attComp.dialogs.assignBtn')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

// ─── Manual Punch Dialog ──────────────────────────────────────────────────────

export const ManualPunchDialog = ({
  open, onClose,
  entry,
  manualPunchTime, setManualPunchTime,
  manualPunchDir, setManualPunchDir,
  manualPunchReason, setManualPunchReason,
  addingPunch, onAdd,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = getDateFnsLocale(i18n.language);
  return (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5 text-muted-foreground" />
          {t('attComp.dialogs.manualTitle')}
        </DialogTitle>
      </DialogHeader>
      {entry && (
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{entry.first_name} {entry.last_name}</span>
            {' · '}
            {format(parseISO(entry.date + 'T00:00:00'), 'EEEE d MMMM', { locale: dateLocale })}
          </p>
          <div className="space-y-2">
            <Label>{t('attComp.dialogs.hourLabel')}</Label>
            <Input type="time" value={manualPunchTime} onChange={e => setManualPunchTime(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t('attComp.dialogs.directionLabel')}</Label>
            <Select value={manualPunchDir} onValueChange={setManualPunchDir}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">{t('attComp.entry')}</SelectItem>
                <SelectItem value="1">{t('attComp.exit')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('attComp.dialogs.reasonLabel')} <span className="text-muted-foreground text-xs">{t('attComp.dialogs.optional')}</span></Label>
            <Input placeholder={t('attComp.dialogs.reasonPlaceholder')} value={manualPunchReason} onChange={e => setManualPunchReason(e.target.value)} />
          </div>
          <p className="text-xs text-muted-foreground bg-muted rounded px-2 py-1 flex items-center gap-1">
            <PencilLine className="h-3 w-3 shrink-0" />
            {t('attComp.dialogs.manualNote')}
          </p>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={onAdd} disabled={!manualPunchTime || addingPunch}
          className="gap-2">
          {addingPunch ? <><RefreshCw className="h-4 w-4 animate-spin" /> {t('attComp.dialogs.adding')}</> : t('attComp.dialogs.addBtn')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

// ─── Absence Reason Dialog ────────────────────────────────────────────────────

export const AbsenceReasonDialog = ({
  open, onClose,
  entry,
  absenceReasons,
  selectedReasonId, setSelectedReasonId,
  assigningAbsence, onAssign,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = getDateFnsLocale(i18n.language);
  return (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FileX2 className="h-5 w-5 text-red-500" />
          {t('attComp.dialogs.absenceTitle')}
        </DialogTitle>
      </DialogHeader>
      {entry && (
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{entry.first_name} {entry.last_name}</span>
            {' · '}
            {format(parseISO(entry.date + 'T00:00:00'), 'EEEE d MMMM', { locale: dateLocale })}
          </p>
          <div className="space-y-2">
            <Label>{t('attComp.dialogs.reasonLabel')}</Label>
            <Select value={selectedReasonId || 'none'} onValueChange={v => setSelectedReasonId(v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder={t('attComp.dialogs.noReason')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('attComp.dialogs.noReason')}</SelectItem>
                {absenceReasons.map(reason => (
                  <SelectItem key={reason.id} value={String(reason.id)}>{reason.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={onAssign} disabled={assigningAbsence}
          className="bg-red-600 hover:bg-red-700 text-white gap-2">
          {assigningAbsence ? <><RefreshCw className="h-4 w-4 animate-spin" /> {t('attComp.dialogs.saving')}</> : t('attComp.dialogs.saveBtn')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

// ─── Report Dialog (multi-report fullscreen preview + export) ─────────────────

const TONE_CLASS = {
  danger: 'text-red-600 font-semibold',
  warning: 'text-orange-600 font-semibold',
  success: 'text-green-600',
  accent: 'text-purple-600 font-semibold',
  muted: 'text-muted-foreground',
};

export const ReportDialog = ({
  open, onOpenChange,
  companyInfo, dateRangeLabel,
  reports,
  selectedReportId, setSelectedReportId,
  congesLoading,
  exportingPDF, exportCSV, exportExcel, exportPDF,
}) => {
  const { t } = useTranslation();
  const reportList = reports || [];
  const report = reportList.find(r => r.id === selectedReportId) || reportList[0];
  if (!report) return null;
  const rows = report.rows || [];
  const isConges = report.id === 'conges';

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="w-screen h-screen max-w-none rounded-none p-0 flex flex-col gap-0">
      <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-muted-foreground" />
              {t('attComp.dialogs.reportTitle', { defaultValue: 'Rapports de présence' })}
            </DialogTitle>
            <DialogDescription className="sr-only">{report.description}</DialogDescription>
            <p className="text-sm text-muted-foreground mt-0.5">
              {(companyInfo?.legal_name || companyInfo?.trade_name)
                ? <span className="font-semibold text-foreground">{companyInfo.legal_name || companyInfo.trade_name} · </span>
                : null}
              {t('attComp.dialogs.periodPrefix', { defaultValue: 'Période :' })} {dateRangeLabel} · {rows.length} {t('attComp.dialogs.recordsWord', { defaultValue: 'enregistrement(s)' })}
            </p>
          </div>

          <div className="min-w-[280px]">
            <Select value={report.id} onValueChange={setSelectedReportId}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reportList.map(r => {
                  const Icon = r.icon;
                  return (
                    <SelectItem key={r.id} value={r.id}>
                      <span className="flex items-center gap-2">
                        {Icon ? <Icon className="h-4 w-4 text-muted-foreground" /> : null}
                        <span>{r.label}</span>
                        <span className="text-xs text-muted-foreground">({(r.rows || []).length})</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1 px-1">{report.description}</p>
          </div>
        </div>
      </DialogHeader>

      <div className="overflow-auto flex-1 px-4 py-3">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/60">
              {report.columns.map(col => (
                <TableHead
                  key={col.key}
                  className={`whitespace-nowrap text-foreground ${col.align === 'center' ? 'text-center' : ''}`}
                >
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isConges && congesLoading ? (
              <TableRow>
                <TableCell colSpan={report.columns.length} className="text-center py-8 text-muted-foreground">
                  <span className="inline-flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> {t('common.loading', { defaultValue: 'Chargement…' })}</span>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={report.columns.length} className="text-center py-10 text-muted-foreground">
                  {report.emptyText}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow key={`${report.id}-${idx}`} className={idx % 2 === 1 ? 'bg-muted/30' : ''}>
                  {report.columns.map(col => {
                    const tone = col.tone ? col.tone(row) : null;
                    return (
                      <TableCell
                        key={col.key}
                        className={`text-xs whitespace-nowrap ${col.align === 'center' ? 'text-center font-mono' : ''} ${tone ? TONE_CLASS[tone] : ''}`}
                      >
                        {col.value(row)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <DialogFooter className="px-6 py-4 border-t shrink-0 gap-2 flex-wrap">
        <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.close')}</Button>
        <Button variant="outline" onClick={() => exportCSV(report)} className="gap-1">
          <Download className="h-4 w-4" /> CSV
        </Button>
        <Button variant="outline" onClick={() => exportExcel(report)} className="gap-1 border-green-900/40 text-green-400 hover:bg-green-950/20">
          <Download className="h-4 w-4" /> Excel (.xls)
        </Button>
        <Button onClick={() => exportPDF(report)} disabled={exportingPDF} className="gap-1">
          <FileText className={`h-4 w-4 ${exportingPDF ? 'animate-pulse' : ''}`} />
          {exportingPDF ? t('attComp.dialogs.generating', { defaultValue: 'Génération…' }) : 'PDF'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};
